import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Minus, Snowflake, PartyPopper } from "lucide-react";
import api from "../lib/api";
import { useFmt } from "../context/CurrencyContext";
import { useCart } from "../context/CartContext";
import { resolveImageUrl } from "../components/ImageUpload";
import SEO from "../components/SEO";

const DEFAULT_CONFIG = {
  price_per_pop: 650,
  min_units: 48,
  units_per_box: 12,
  max_flavours: 5,
  dry_ice_addon_name: "Dry Ice + Foam Box Packaging",
  dry_ice_addon_price: 5000,
};

export default function PartyBox() {
  const [flavours, setFlavours] = useState([]);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [selection, setSelection] = useState({}); // { flavourName: count }
  const [dryIce, setDryIce] = useState(false);
  const { addItem } = useCart();
  const fmtJMD = useFmt();

  useEffect(() => {
    api.get("/products?build_a_box=true").then((r) => setFlavours(r.data)).catch(() => {});
    api.get("/party-box/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const totalUnits = useMemo(
    () => Object.values(selection).reduce((s, n) => s + n, 0),
    [selection]
  );
  const uniqueFlavoursPicked = Object.values(selection).filter((c) => c > 0).length;
  const popsSubtotal = totalUnits * config.price_per_pop;
  const addonsTotal = dryIce ? config.dry_ice_addon_price : 0;
  const grandTotal = popsSubtotal + addonsTotal;
  const meetsMin = totalUnits >= config.min_units;

  const bumpFlavour = (name, delta) => {
    setSelection((s) => {
      const current = s[name] || 0;
      const isAlreadyPicked = current > 0;
      const wouldExceedFlavourCap =
        !isAlreadyPicked && delta > 0 && uniqueFlavoursPicked >= config.max_flavours;
      if (wouldExceedFlavourCap) {
        toast.error(`You can pick a maximum of ${config.max_flavours} flavours per party box`);
        return s;
      }
      // Flavours are sold by the box of `units_per_box` (default 12) — each tap
      // adds / removes a full box of that flavour.
      const step = config.units_per_box || 12;
      const next = Math.max(0, current + delta * step);
      const copy = { ...s };
      if (next === 0) delete copy[name];
      else copy[name] = next;
      return copy;
    });
  };

  const addToCart = () => {
    if (!meetsMin) {
      toast.error(`Minimum ${config.min_units} pops (4 boxes) required for a Party Box`);
      return;
    }
    const flavourList = Object.entries(selection).flatMap(([n, c]) => Array(c).fill(n));
    addItem({
      type: "party_box",
      ref_id: null,
      name: `Party Box · ${totalUnits} pops`,
      price: popsSubtotal,
      quantity: 1,
      custom_flavours: flavourList,
    });
    if (dryIce) {
      addItem({
        type: "addon",
        ref_id: "dry_ice",
        name: config.dry_ice_addon_name,
        price: config.dry_ice_addon_price,
        quantity: 1,
      });
    }
    toast.success(`Party Box (${totalUnits} pops) added to cart`);
    setSelection({});
    setDryIce(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 py-12 sm:py-16" data-testid="party-box-page">
      <SEO title="Party Box — Fruit Popz for events" description="Order Fruit Popz in bulk for birthdays, corporate functions, school events and family gatherings. Minimum 48 pops, up to 5 flavours." />

      <div className="mb-10 sm:mb-12 max-w-3xl">
        <div className="overline text-zinc-500 mb-3 inline-flex items-center gap-2">
          <PartyPopper size={14} className="text-[#FFA62B]" /> Party Box
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
          Bring a pop of Jamaica<br />
          <span style={{ color: "#F74B64" }}>to the celebration.</span>
        </h1>
        <p className="mt-4 sm:mt-6 text-zinc-700 text-base sm:text-lg leading-relaxed">
          Our Party Box is made for birthdays, corporate events, school treats, family gatherings,
          parties, and special occasions. Minimum order starts at <strong>{config.min_units} Fruit
          Popz</strong> ({Math.ceil(config.min_units / config.units_per_box)} boxes of {config.units_per_box}),
          with the option to select up to <strong>{config.max_flavours} flavours</strong>. Each flavour
          ships in <strong>boxes of {config.units_per_box}</strong> — tap <kbd>+</kbd> to add a box.
        </p>
        <p className="mt-3 text-sm text-zinc-500">
          {fmtJMD(config.price_per_pop)} per pop · delivery confirmed by our team after we review your order.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Flavour picker */}
        <div className="lg:col-span-8">
          <div className="overline text-zinc-500 mb-4">Pick up to {config.max_flavours} flavours</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {flavours.map((f) => {
              const count = selection[f.name] || 0;
              const isPicked = count > 0;
              const flavourCapReached = !isPicked && uniqueFlavoursPicked >= config.max_flavours;
              return (
                <motion.div
                  layout
                  key={f.id}
                  className={`rounded-2xl border ${isPicked ? "border-[#F74B64]" : "border-zinc-200"} bg-white overflow-hidden transition ${flavourCapReached ? "opacity-50" : ""}`}
                  data-testid={`party-flavour-${f.id}`}
                >
                  <div className="aspect-square bg-zinc-100">
                    {f.image_url && (
                      <img src={resolveImageUrl(f.image_url)} alt={f.name} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="p-3 sm:p-4">
                    <div className="font-semibold text-sm sm:text-base truncate">{f.name}</div>
                    <div className="text-xs text-zinc-500">{fmtJMD(f.price * (config.units_per_box || 12))} / box of {config.units_per_box || 12}</div>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => bumpFlavour(f.name, -1)}
                        disabled={count === 0}
                        className="w-7 h-7 rounded-full border border-zinc-200 inline-flex items-center justify-center disabled:opacity-30"
                        data-testid={`party-flavour-minus-${f.id}`}
                        title={`Remove a box of ${config.units_per_box || 12}`}
                      >
                        <Minus size={14} />
                      </button>
                      <div className="font-semibold text-sm tabular-nums leading-tight text-center" data-testid={`party-flavour-count-${f.id}`}>
                        {count}
                        {count > 0 && (
                          <div className="text-[10px] font-normal text-zinc-500">
                            {Math.round(count / (config.units_per_box || 12))} box{count / (config.units_per_box || 12) === 1 ? "" : "es"}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => bumpFlavour(f.name, +1)}
                        disabled={flavourCapReached}
                        className="w-7 h-7 rounded-full bg-[#F74B64] text-white inline-flex items-center justify-center disabled:opacity-30"
                        data-testid={`party-flavour-plus-${f.id}`}
                        title={`Add a box of ${config.units_per_box || 12}`}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Summary */}
        <aside className="lg:col-span-4">
          <div className="sticky top-24 rounded-2xl border-2 border-zinc-900 bg-white p-5 sm:p-6" data-testid="party-summary">
            <div className="overline text-zinc-500 mb-2">Your party box</div>
            <div className="font-display text-3xl sm:text-4xl font-extrabold">
              {totalUnits} <span className="text-base text-zinc-500 font-normal">/ {config.min_units} min pops</span>
            </div>

            <div className="mt-4 h-2 rounded-full bg-zinc-100 overflow-hidden">
              <div
                className="h-full transition-all"
                style={{
                  width: `${Math.min(100, (totalUnits / config.min_units) * 100)}%`,
                  background: meetsMin ? "#009A49" : "#F74B64",
                }}
              />
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              {uniqueFlavoursPicked}/{config.max_flavours} flavours picked
            </div>

            {totalUnits > 0 && (
              <ul className="mt-4 space-y-1 text-sm">
                {Object.entries(selection).map(([n, c]) => (
                  <li key={n} className="flex justify-between gap-2 text-zinc-700">
                    <span className="truncate">{c}× {n}</span>
                    <span className="font-semibold tabular-nums">{fmtJMD(c * config.price_per_pop)}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-5 pt-5 border-t border-zinc-200">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="party-addon-dryice">
                <input
                  type="checkbox"
                  checked={dryIce}
                  onChange={(e) => setDryIce(e.target.checked)}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm inline-flex items-center gap-1.5">
                    <Snowflake size={14} className="text-[#009A49]" />
                    {config.dry_ice_addon_name}
                  </div>
                  <div className="text-xs text-zinc-500 leading-relaxed">
                    Keep your pops frozen longer in transit and at the venue. +{fmtJMD(config.dry_ice_addon_price)}
                  </div>
                </div>
              </label>
            </div>

            <div className="mt-5 pt-5 border-t border-zinc-200 space-y-1 text-sm">
              <div className="flex justify-between text-zinc-500">
                <span>{totalUnits} pop{totalUnits === 1 ? "" : "s"} × {fmtJMD(config.price_per_pop)}</span>
                <span>{fmtJMD(popsSubtotal)}</span>
              </div>
              {dryIce && (
                <div className="flex justify-between text-zinc-500">
                  <span>Dry-ice packaging</span>
                  <span>{fmtJMD(addonsTotal)}</span>
                </div>
              )}
              <div className="flex justify-between font-display text-xl font-extrabold pt-2 border-t border-zinc-200 mt-2">
                <span>Total</span>
                <span data-testid="party-total">{fmtJMD(grandTotal)}</span>
              </div>
            </div>

            <button
              onClick={addToCart}
              disabled={!meetsMin}
              className={`mt-5 w-full inline-flex justify-center items-center gap-2 rounded-full px-5 py-3 font-semibold text-sm transition-colors ${
                meetsMin
                  ? "bg-[#F74B64] hover:bg-[#e23a52] text-white"
                  : "bg-zinc-200 text-zinc-500 cursor-not-allowed"
              }`}
              data-testid="party-add-to-cart"
            >
              {meetsMin
                ? "Order Your Party Box"
                : `Add ${config.min_units - totalUnits} more pop${config.min_units - totalUnits === 1 ? "" : "s"}`}
            </button>

            <p className="mt-3 text-[11px] text-zinc-500 leading-relaxed text-center">
              Delivery fee will be confirmed by our team after we review your order.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
